import {
  Navigate,
  Outlet,
  Route,
  HashRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import Home from "./pages/home";
import Login from "./pages/login";
import Logout from "./pages/logout";
import StaffingDashboard from "./pages/staffing-dashboard";
import ProviderDataPage from "./pages/provider-data";
import EprMetricsAdminPage from './pages/epr-metrics-admin';
import MPDDataPanel from "./pages/embedded-app/MPDDataPanel";
import EmbeddedOpenings from "./pages/embedded-app/Openings";
import EmbeddedOpeningDetail from "./pages/embedded-app/openings/OpeningDetail";
import EmbeddedNotifications from "./pages/embedded-app/Notifications";
import EmbeddedNotificationDetail from "./pages/embedded-app/notifications/NotificationDetail";
import "./App.css";
import Header from "./components/header";
import { IMySettings } from "./components/settings/IMySettings";
import { useContext, useEffect, useState } from "react";
import {
  InteractionStatus,
  PublicClientApplication,
} from "@azure/msal-browser";
import {
  MsalProvider,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import {
  CurrentUserContext,
  CurrentUserProvider,
} from "./context/CurrentUserContext";
import { ApiProvider } from "./context/ApiContext";
import { SuggestionsProvider } from "./context/SuggestionsContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { FormProvider, useForm } from "./context/FormContext";
import { EmbeddedAuthGate } from "./services/auth/EmbeddedAuthGate";
import { EmbeddedMsalSsoGate } from "./services/auth/EmbeddedMsalSsoGate";
import SuggestionForm from "./components/suggestions/SuggestionForm";
import { TooltipProvider } from "./components/ui/tooltip";
import { WidgetProvider } from "./context/WidgetContext";
import { StaffingProvider } from "./context/StaffingContext";
import WidgetBar from "./components/widgets/WidgetBar";
import { usePbsLiveModalInterop } from "./hooks/usePbsLiveModalInterop";

declare global {
  interface Window {
    _spPageContextInfo: any;
    initializePbsWidget: any;
    openCopilotChat: any;
    openChatDirectlyWithTreatmentGroup: any;
    openStaffingGroupDirectlyWithReferenceId: any;
  }
}

const LoadingCircle = (props: any) => {
  return (
    <div className="text-center pt-12">
      <h1 className="font-light text-2xl">PBS Connect</h1>
      <div className="p-5 pt-12 flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary" />
        <span className="text-lg font-light">{props.label}</span>
      </div>
    </div>
  );
};

function AuthenticatedLayout({
  settings,
  onSettingsChanged,
}: {
  settings: IMySettings;
  onSettingsChanged: (s: IMySettings) => void;
}) {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const location = useLocation();

  if (!isAuthenticated) {
    // MSAL is still starting up or processing the auth-code return leg;
    // redirecting to /login here would break handleRedirectPromise.
    if (inProgress !== InteractionStatus.None) {
      return <LoadingCircle label="Signing In ... Hang Tight" />;
    }
    const target = location.pathname + location.search;
    const query =
      target !== "/" ? `?redirect=${encodeURIComponent(target)}` : "";
    return <Navigate to={`/login${query}`} replace />;
  }

  return (
    <>
      <div
        style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}
      >
        <Header settings={settings} onSettingsChanged={onSettingsChanged} />
      </div>
      <div className="pt-[68px]">
        <Outlet />
      </div>
      <WidgetBar />
    </>
  );
}

function MainContent() {
  const { currentUser, originalUser } = useContext(CurrentUserContext);
  const { isDarkMode } = useTheme();
  const { activeForm, isFormOpen, suggestion, closeForm } = useForm();

  const [settings, setSettings] = useState<IMySettings>({
    enableOnBehalfOf: false,
  });

  // Keep the PBS Live widget interactive/typeable over Radix modals & panels.
  usePbsLiveModalInterop();

  const onSettingsChanged = (settings: IMySettings) => {
    setSettings(settings);
  };

  const isEmbeddedApp = window.location.hash.startsWith("#/embedded-app/");

  // Embedded pages run inside a host webview and must not show the externally
  // injected PBS Live / Copilot widget. Tag <html> so CSS can hide #root-pbslive
  // (the widget self-mounts even though we skip initializePbsWidget below).
  useEffect(() => {
    document.documentElement.classList.toggle("embedded-app", isEmbeddedApp);
    return () => document.documentElement.classList.remove("embedded-app");
  }, [isEmbeddedApp]);

  useEffect(() => {
    // Embedded views take their theme from the url, so don't let them restyle the host's widget
    if (!isEmbeddedApp) {
      window.localStorage.setItem(
        "pbslive-ui-theme",
        isDarkMode ? "dark" : "light",
      );
    }
    if (currentUser?.LoginName && !isEmbeddedApp) {
      const userForWidget = originalUser?.LoginName
        ? originalUser
        : currentUser;
      if (!window["_spPageContextInfo"]) {
        window["_spPageContextInfo"] = {
          userLoginName: "positive\\" + currentUser?.LoginName,
          userDisplayName: currentUser?.FullName,
        };
        if (window["initializePbsWidget"]) {
          window["initializePbsWidget"]({
            username: userForWidget?.LoginName,
            theme: isDarkMode ? "dark" : "light",
          });
        }
      }
    }
  }, [currentUser, originalUser, isDarkMode, isEmbeddedApp]);

  return (
    <>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route
            element={
              <AuthenticatedLayout
                settings={settings}
                onSettingsChanged={onSettingsChanged}
              />
            }
          >
            <Route index element={<Home settings={settings} />} />
            <Route
              path="/staffing-dashboard"
              element={
                <StaffingProvider>
                  <StaffingDashboard />
                </StaffingProvider>
              }
            />
            <Route path="/provider-data" element={<ProviderDataPage />} />
            <Route path="/epr-metrics-admin" element={<EprMetricsAdminPage />} />
          </Route>
          {/* mpd calls the MyProviderData API (MSAL bearer token), so it can't ride
              the cookie session like the routes below - it signs in silently with
              the sid/login_hint the PBS Live host appends to the URL. */}
          <Route
            path="/embedded-app/mpd"
            element={
              <EmbeddedMsalSsoGate loadingLabel="Loading meeting data...">
                <MPDDataPanel />
              </EmbeddedMsalSsoGate>
            }
          />
          {/* Openings calls the enterprise apiClient, so it can ride the host's cookie session */}
          <Route
            path="/embedded-app/openings"
            element={
              <EmbeddedAuthGate loadingLabel="Loading openings...">
                <EmbeddedOpenings />
              </EmbeddedAuthGate>
            }
          />
          <Route
            path="/embedded-app/openings/detail"
            element={
              <EmbeddedAuthGate loadingLabel="Loading opening...">
                <EmbeddedOpeningDetail />
              </EmbeddedAuthGate>
            }
          />
          <Route
            path="/embedded-app/notifications"
            element={
              <EmbeddedAuthGate>
                <EmbeddedNotifications />
              </EmbeddedAuthGate>
            }
          />
          <Route
            path="/embedded-app/notifications/detail"
            element={
              <EmbeddedAuthGate>
                <EmbeddedNotificationDetail />
              </EmbeddedAuthGate>
            }
          />
        </Routes>
      </Router>

      {isFormOpen && activeForm && suggestion && (
        <SuggestionForm
          isOpen={isFormOpen}
          onDismiss={closeForm}
          form={activeForm}
          suggestion={suggestion}
        />
      )}
    </>
  );
}

interface IAppProps {
  instance: PublicClientApplication;
}
export const App: React.FC<IAppProps> = ({ instance }) => {
  return (
    <MsalProvider instance={instance}>
      <ThemeProvider>
        <ApiProvider msalApp={instance}>
          <CurrentUserProvider>
            <SuggestionsProvider>
              <FormProvider>
                <TooltipProvider>
                  <WidgetProvider>
                    <MainContent />
                  </WidgetProvider>
                </TooltipProvider>
              </FormProvider>
            </SuggestionsProvider>
          </CurrentUserProvider>
        </ApiProvider>
      </ThemeProvider>
    </MsalProvider>
  );
};
